/**
 * Static analysis for CSS stabilizer codes.
 * All computations are over GF(2).
 */
import { StabilizerCode, Stabilizer, PauliType, MeasurementGate, LogicalOp, PauliTerm, defaultMeasurementCircuit, flattenCircuit } from '../types/stabilizer';

// ── GF(2) linear algebra ──

export type BitVec = boolean[];
export type BitMatrix = BitVec[];

export function rowEchelon(mat: BitMatrix, nCols: number): { rref: BitMatrix; rank: number; pivotCols: number[] } {
  const m = mat.map((r) => [...r]);
  const pivotCols: number[] = [];
  let row = 0;
  for (let col = 0; col < nCols && row < m.length; col++) {
    let pivotRow = -1;
    for (let r = row; r < m.length; r++) {
      if (m[r][col]) { pivotRow = r; break; }
    }
    if (pivotRow < 0) continue;
    [m[row], m[pivotRow]] = [m[pivotRow], m[row]];
    for (let r = 0; r < m.length; r++) {
      if (r !== row && m[r][col]) {
        for (let c = 0; c < nCols; c++) m[r][c] = m[r][c] !== m[row][c];
      }
    }
    pivotCols.push(col);
    row++;
  }
  return { rref: m, rank: row, pivotCols };
}

/** Find basis of kernel of matrix (rows are equations) */
export function kernel(mat: BitMatrix, nCols: number): BitMatrix {
  const { rref, pivotCols } = rowEchelon(mat, nCols);
  const pivotSet = new Set(pivotCols);
  const freeCols = [];
  for (let c = 0; c < nCols; c++) if (!pivotSet.has(c)) freeCols.push(c);

  // For each free variable, construct a kernel vector
  const basis: BitMatrix = [];
  for (const fc of freeCols) {
    const v: BitVec = Array(nCols).fill(false);
    v[fc] = true;
    // For each pivot row, set the pivot col value
    for (let r = 0; r < pivotCols.length; r++) {
      if (rref[r][fc]) v[pivotCols[r]] = true;
    }
    basis.push(v);
  }
  return basis;
}

/** Check if vector is in rowspace of matrix */
export function inRowspace(vec: BitVec, mat: BitMatrix, nCols: number): boolean {
  // Augment matrix with vec as extra row, check if rank changes
  const augmented = [...mat.map((r) => [...r]), [...vec]];
  const r1 = rowEchelon(mat, nCols).rank;
  const r2 = rowEchelon(augmented, nCols).rank;
  return r1 === r2;
}

/**
 * Brute-force code distance verification for CSS codes.
 * d_X = min weight of vector in ker(H_Z) \ rowspace(H_X)
 * d_Z = min weight of vector in ker(H_X) \ rowspace(H_Z)
 * d = min(d_X, d_Z)
 *
 * Enumerates all 2^dim combinations of kernel basis vectors.
 * Only feasible for small codes (dim ≤ ~20).
 */
export function computeCodeDistance(
  code: StabilizerCode,
): { n: number; k: number; dX: number; dZ: number; d: number; feasible: boolean } {
  const n = code.qubits.length;
  if (n === 0) return { n: 0, k: 0, dX: Infinity, dZ: Infinity, d: Infinity, feasible: true };

  const qubitIds = code.qubits.map((q) => q.id);
  const Hx = buildParityMatrix(code.stabilizers, qubitIds, 'X');
  const Hz = buildParityMatrix(code.stabilizers, qubitIds, 'Z');

  const rankHx = Hx.length > 0 ? rowEchelon(Hx, n).rank : 0;
  const rankHz = Hz.length > 0 ? rowEchelon(Hz, n).rank : 0;
  const k = n - rankHx - rankHz;

  // Find minimum weight logical operators by enumeration
  const findMinWeight = (kerBasis: BitMatrix, stabMatrix: BitMatrix): number => {
    const dim = kerBasis.length;
    if (dim === 0) return Infinity;
    // Safety: don't enumerate if dim > 20
    if (dim > 20) return -1; // signal infeasible

    let minW = Infinity;
    // Enumerate all non-zero linear combinations of basis vectors
    const total = (1 << dim);
    for (let mask = 1; mask < total; mask++) {
      const vec: BitVec = Array(n).fill(false);
      for (let b = 0; b < dim; b++) {
        if (mask & (1 << b)) {
          for (let c = 0; c < n; c++) vec[c] = vec[c] !== kerBasis[b][c];
        }
      }
      const weight = vec.filter(Boolean).length;
      if (weight >= minW) continue; // prune
      // Check if NOT in stabilizer rowspace (= is a logical operator)
      if (stabMatrix.length === 0 || !inRowspace(vec, stabMatrix, n)) {
        minW = weight;
      }
    }
    return minW;
  };

  // ker(H_Z) \ rowspace(H_X) → X-distance
  const kerHz = Hz.length > 0 ? kernel(Hz, n) : Array.from({ length: n }, (_, i) => {
    const v: BitVec = Array(n).fill(false); v[i] = true; return v;
  });
  // ker(H_X) \ rowspace(H_Z) → Z-distance
  const kerHx = Hx.length > 0 ? kernel(Hx, n) : Array.from({ length: n }, (_, i) => {
    const v: BitVec = Array(n).fill(false); v[i] = true; return v;
  });

  const dX = findMinWeight(kerHz, Hx);
  const dZ = findMinWeight(kerHx, Hz);
  const feasible = dX !== -1 && dZ !== -1;

  return { n, k, dX: dX === -1 ? Infinity : dX, dZ: dZ === -1 ? Infinity : dZ, d: Math.min(dX === -1 ? Infinity : dX, dZ === -1 ? Infinity : dZ), feasible };
}

// ── Build parity check matrices ──

export function buildParityMatrix(stabilizers: Stabilizer[], qubitIds: string[], type: PauliType): BitMatrix {
  const idxMap = new Map(qubitIds.map((id, i) => [id, i]));
  return stabilizers
    .filter((s) => s.type === type)
    .map((s) => {
      const row: BitVec = Array(qubitIds.length).fill(false);
      for (const qid of s.qubitIds) {
        const idx = idxMap.get(qid);
        if (idx !== undefined) row[idx] = true;
      }
      return row;
    });
}

// ── 1. Commutativity check ──

export interface CommutativityResult {
  allCommute: boolean;
  violations: { stabA: Stabilizer; stabB: Stabilizer; sharedQubits: number[] }[];
}

export function checkCommutativity(code: StabilizerCode): CommutativityResult {
  const violations: CommutativityResult['violations'] = [];
  const qubitIndexMap = new Map(code.qubits.map((q) => [q.id, q.index]));

  for (let i = 0; i < code.stabilizers.length; i++) {
    for (let j = i + 1; j < code.stabilizers.length; j++) {
      const a = code.stabilizers[i];
      const b = code.stabilizers[j];
      // Same type always commute
      if (a.type === b.type) continue;
      // Different type: commute iff even overlap
      const setB = new Set(b.qubitIds);
      const shared = a.qubitIds.filter((id) => setB.has(id));
      if (shared.length % 2 !== 0) {
        violations.push({
          stabA: a,
          stabB: b,
          sharedQubits: shared.map((id) => qubitIndexMap.get(id) ?? -1).sort((a, b) => a - b),
        });
      }
    }
  }
  return { allCommute: violations.length === 0, violations };
}

// ── 2. Logical operators ──

export interface LogicalOperator {
  type: PauliType;
  qubitIndices: number[];
}

export interface LogicalOperatorResult {
  k: number; // number of logical qubits
  logicalXOps: LogicalOperator[];
  logicalZOps: LogicalOperator[];
  rankHx: number;
  rankHz: number;
}

export function findLogicalOperators(code: StabilizerCode): LogicalOperatorResult {
  const n = code.qubits.length;
  const qubitIds = code.qubits.map((q) => q.id);
  const qubitIndices = code.qubits.map((q) => q.index);

  const Hx = buildParityMatrix(code.stabilizers, qubitIds, 'X');
  const Hz = buildParityMatrix(code.stabilizers, qubitIds, 'Z');

  const rankHx = Hx.length > 0 ? rowEchelon(Hx, n).rank : 0;
  const rankHz = Hz.length > 0 ? rowEchelon(Hz, n).rank : 0;
  const k = n - rankHx - rankHz;

  // Logical X operators: in ker(Hz) but not in rowspace(Hx)
  const kerHz = Hz.length > 0 ? kernel(Hz, n) : Array.from({ length: n }, (_, i) => {
    const v: BitVec = Array(n).fill(false); v[i] = true; return v;
  });

  const logicalXOps: LogicalOperator[] = [];
  for (const vec of kerHz) {
    if (Hx.length === 0 || !inRowspace(vec, Hx, n)) {
      const indices = vec.map((b, i) => b ? qubitIndices[i] : -1).filter((i) => i >= 0);
      if (indices.length > 0) logicalXOps.push({ type: 'X', qubitIndices: indices });
    }
  }

  // Logical Z operators: in ker(Hx) but not in rowspace(Hz)
  const kerHx = Hx.length > 0 ? kernel(Hx, n) : Array.from({ length: n }, (_, i) => {
    const v: BitVec = Array(n).fill(false); v[i] = true; return v;
  });

  const logicalZOps: LogicalOperator[] = [];
  for (const vec of kerHx) {
    if (Hz.length === 0 || !inRowspace(vec, Hz, n)) {
      const indices = vec.map((b, i) => b ? qubitIndices[i] : -1).filter((i) => i >= 0);
      if (indices.length > 0) logicalZOps.push({ type: 'Z', qubitIndices: indices });
    }
  }

  return { k, logicalXOps: logicalXOps.slice(0, Math.max(k, 1)), logicalZOps: logicalZOps.slice(0, Math.max(k, 1)), rankHx, rankHz };
}

// ── 3. Resolve measurement circuits (user-defined or default) ──

export interface ResolvedCircuit {
  stabilizer: Stabilizer;
  stabIndex: number;
  ancillaIndex: number;
  circuit: MeasurementGate[];
  dataQubitIndices: number[];
}

export function resolveCircuits(code: StabilizerCode): ResolvedCircuit[] {
  const qubitIndexMap = new Map(code.qubits.map((q) => [q.id, q.index]));
  const nData = code.qubits.length;

  return code.stabilizers.map((stab, i) => {
    const ancillaIndex = nData + i;
    const dataQubitIndices = stab.qubitIds
      .map((id) => qubitIndexMap.get(id) ?? -1)
      .filter((x) => x >= 0)
      .sort((a, b) => a - b);

    let circuit: MeasurementGate[];
    if (stab.circuit) {
      // Use visual circuit model, flatten to gate list
      circuit = flattenCircuit(stab.circuit);
    } else if (stab.measurementCircuit) {
      // Legacy flat gate list
      circuit = stab.measurementCircuit;
    } else {
      circuit = defaultMeasurementCircuit(stab.type, dataQubitIndices, ancillaIndex);
    }

    return { stabilizer: stab, stabIndex: i, ancillaIndex, circuit, dataQubitIndices };
  });
}

// ── 4. Scheduling (user-defined order or auto graph-coloring) ──

export interface ScheduleEntry {
  order: number;
  stabId: string;
  stabIndex: number;
}

export function resolveSchedule(code: StabilizerCode): ScheduleEntry[] {
  if (code.scheduleOrder && code.scheduleOrder.length > 0) {
    // User-defined order: sequential
    const idToIdx = new Map(code.stabilizers.map((s, i) => [s.id, i]));
    return code.scheduleOrder
      .map((id, order) => ({ order, stabId: id, stabIndex: idToIdx.get(id) ?? -1 }))
      .filter((e) => e.stabIndex >= 0);
  }
  // Fallback: sequential by definition order
  return code.stabilizers.map((s, i) => ({ order: i, stabId: s.id, stabIndex: i }));
}

// ── 5. Circuit compilation ──

function emitStimGate(g: MeasurementGate): string {
  if (g.gate === 'CX' || g.gate === 'CZ') {
    return `${g.gate} ${g.targets[0]} ${g.targets[1]}`;
  }
  return `${g.gate} ${g.targets[0]}`;
}

function emitQiskitGate(g: MeasurementGate, nData: number, measIdx: { v: number }): string {
  const qStr = (idx: number) => idx < nData ? `data[${idx}]` : `anc[${idx - nData}]`;
  switch (g.gate) {
    case 'H': return `qc.h(${qStr(g.targets[0])})`;
    case 'CX': return `qc.cx(${qStr(g.targets[0])}, ${qStr(g.targets[1])})`;
    case 'CZ': return `qc.cz(${qStr(g.targets[0])}, ${qStr(g.targets[1])})`;
    case 'R': return `qc.reset(${qStr(g.targets[0])})`;
    case 'M': {
      const line = `qc.measure(${qStr(g.targets[0])}, syn[${measIdx.v}])`;
      measIdx.v++;
      return line;
    }
    default: return `# unknown gate ${g.gate}`;
  }
}

export function compileToStim(code: StabilizerCode): string {
  const circuits = resolveCircuits(code);
  const schedule = resolveSchedule(code);
  const nData = code.qubits.length;
  const nAnc = code.stabilizers.length;
  const lines: string[] = [];

  lines.push(`# Stabilizer code: ${code.name}`);
  lines.push(`# ${nData} data qubits, ${nAnc} ancilla qubits`);
  lines.push(`# [[${nData}, k, d]] CSS code`);
  lines.push('');

  for (const q of code.qubits) {
    lines.push(`QUBIT_COORDS(${q.x.toFixed(0)}, ${q.y.toFixed(0)}) ${q.index}`);
  }
  for (let i = 0; i < nAnc; i++) {
    lines.push(`QUBIT_COORDS(0, 0) ${nData + i}  # ancilla for ${code.stabilizers[i].type}-stab`);
  }
  lines.push('');

  const ancillaQubits = Array.from({ length: nAnc }, (_, i) => nData + i);
  lines.push(`R ${ancillaQubits.join(' ')}`);
  lines.push('');

  lines.push('# ── Stabilizer measurements (user-defined order) ──');
  let measCount = 0;
  for (const entry of schedule) {
    const rc = circuits[entry.stabIndex];
    if (!rc) continue;
    const name = rc.stabilizer.name || `${rc.stabilizer.type}_stab${entry.stabIndex}`;
    lines.push(`# [${entry.order}] ${name}  (qubits: ${rc.dataQubitIndices.join(',')})`);

    for (const g of rc.circuit) {
      lines.push(emitStimGate(g));
    }

    // Detector after measurement
    measCount++;
    lines.push(`DETECTOR(${rc.dataQubitIndices.join(',')}) rec[-1]  # ${name}`);
    lines.push('');
  }

  lines.push(`R ${ancillaQubits.join(' ')}`);
  return lines.join('\n');
}

export function compileToQiskit(code: StabilizerCode): string {
  const circuits = resolveCircuits(code);
  const schedule = resolveSchedule(code);
  const nData = code.qubits.length;
  const nAnc = code.stabilizers.length;
  const lines: string[] = [];

  lines.push('from qiskit import QuantumCircuit, QuantumRegister, ClassicalRegister');
  lines.push('');
  lines.push(`# Stabilizer code: ${code.name}`);
  lines.push(`# [[${nData}, k, d]] CSS code`);
  lines.push('');
  lines.push(`data = QuantumRegister(${nData}, "data")`);
  lines.push(`anc = QuantumRegister(${nAnc}, "anc")`);
  lines.push(`syn = ClassicalRegister(${nAnc}, "syn")`);
  lines.push('qc = QuantumCircuit(data, anc, syn)');
  lines.push('');

  lines.push('# ── Stabilizer measurements (user-defined order) ──');
  const measIdx = { v: 0 };
  for (const entry of schedule) {
    const rc = circuits[entry.stabIndex];
    if (!rc) continue;
    const name = rc.stabilizer.name || `${rc.stabilizer.type}_stab${entry.stabIndex}`;
    lines.push(`# [${entry.order}] ${name}  (qubits: ${rc.dataQubitIndices.join(',')})`);

    for (const g of rc.circuit) {
      lines.push(emitQiskitGate(g, nData, measIdx));
    }

    lines.push('qc.barrier()');
    lines.push('');
  }

  for (let i = 0; i < nAnc; i++) {
    lines.push(`qc.reset(anc[${i}])`);
  }

  return lines.join('\n');
}

// ── 6. Verify user-specified logical operators ──
//
// A general Pauli operator P = ⊗ᵢ σᵢ on n qubits is represented in the
// symplectic picture as two n-bit vectors (xVec, zVec):
//   qubit i has σᵢ = X^{xVec[i]} · Z^{zVec[i]}
//   I → (0,0), X → (1,0), Z → (0,1), Y → (1,1)
//
// Two Paulis P=(x₁,z₁), Q=(x₂,z₂) commute iff their symplectic inner
// product vanishes:  ⟨P,Q⟩ = Σᵢ (x₁ᵢ·z₂ᵢ + z₁ᵢ·x₂ᵢ) mod 2 = 0
//
// CSS stabilizers are pure-X or pure-Z, so an X-stabilizer has (x,0) and
// a Z-stabilizer has (0,z).

interface SymplecticVec { xVec: BitVec; zVec: BitVec }

function termsToSymplectic(terms: PauliTerm[], n: number, qubitIndexToPos: Map<number, number>): SymplecticVec {
  const xVec: BitVec = Array(n).fill(false);
  const zVec: BitVec = Array(n).fill(false);
  for (const t of terms) {
    const pos = qubitIndexToPos.get(t.qubitIndex);
    if (pos === undefined) continue;
    if (t.pauli === 'X') { xVec[pos] = true; }
    else if (t.pauli === 'Z') { zVec[pos] = true; }
    else if (t.pauli === 'Y') { xVec[pos] = true; zVec[pos] = true; }
  }
  return { xVec, zVec };
}

/** Symplectic inner product mod 2. Returns true if the two operators anticommute. */
function symplecticAnticommute(a: SymplecticVec, b: SymplecticVec, n: number): boolean {
  let sum = 0;
  for (let i = 0; i < n; i++) {
    if (a.xVec[i] && b.zVec[i]) sum++;
    if (a.zVec[i] && b.xVec[i]) sum++;
  }
  return sum % 2 === 1;
}

/** Build symplectic vec for a CSS stabilizer (pure X or pure Z). */
function stabToSymplectic(stab: Stabilizer, n: number, qubitIdToPos: Map<string, number>): SymplecticVec {
  const xVec: BitVec = Array(n).fill(false);
  const zVec: BitVec = Array(n).fill(false);
  for (const qid of stab.qubitIds) {
    const pos = qubitIdToPos.get(qid);
    if (pos === undefined) continue;
    if (stab.type === 'X') xVec[pos] = true;
    else zVec[pos] = true;
  }
  return { xVec, zVec };
}

export interface LogicalOpVerification {
  op: LogicalOp;
  commutesWithAll: boolean;           // Must commute with every stabilizer
  notInStabilizerGroup: boolean;      // Must NOT be a product of stabilizers
  anticommutePairIdx: number | null;  // Index of the partner it anticommutes with
}

export interface LogicalOpsVerificationResult {
  verifications: LogicalOpVerification[];
  allValid: boolean;
}

export function verifyLogicalOperators(code: StabilizerCode): LogicalOpsVerificationResult {
  const ops = code.logicalOperators ?? [];
  if (ops.length === 0) return { verifications: [], allValid: true };

  const n = code.qubits.length;
  const qubitIndexToPos = new Map(code.qubits.map((q, i) => [q.index, i]));
  const qubitIdToPos = new Map(code.qubits.map((q, i) => [q.id, i]));

  // Symplectic vecs for all stabilizers
  const stabSymp = code.stabilizers.map((s) => stabToSymplectic(s, n, qubitIdToPos));

  // Symplectic vecs for all logical operators
  const opSymp = ops.map((op) => termsToSymplectic(op.terms, n, qubitIndexToPos));

  // For "not in stabilizer group" check, we need to verify the operator cannot
  // be written as a product of stabilizers. In the symplectic picture, product =
  // XOR of bit vectors. We check if the X-part is in rowspace(Hx) AND Z-part
  // is in rowspace(Hz) simultaneously. If both are, it's in the stabilizer group.
  const qubitIds = code.qubits.map((q) => q.id);
  const Hx = buildParityMatrix(code.stabilizers, qubitIds, 'X');
  const Hz = buildParityMatrix(code.stabilizers, qubitIds, 'Z');

  const verifications: LogicalOpVerification[] = ops.map((op, idx) => {
    const sv = opSymp[idx];

    // 1. Commutativity with all stabilizers (symplectic inner product)
    let commutesWithAll = true;
    for (const ss of stabSymp) {
      if (symplecticAnticommute(sv, ss, n)) { commutesWithAll = false; break; }
    }

    // 2. Not in stabilizer group: X-part must not be in rowspace(Hx) or Z-part
    //    must not be in rowspace(Hz). If both are expressible, it's a stabilizer.
    const xInHx = Hx.length === 0 ? sv.xVec.every((b) => !b) : inRowspace(sv.xVec, Hx, n);
    const zInHz = Hz.length === 0 ? sv.zVec.every((b) => !b) : inRowspace(sv.zVec, Hz, n);
    const notInStabilizerGroup = !(xInHx && zInHz);

    // 3. Find anticommuting partner among other logical operators
    let anticommutePairIdx: number | null = null;
    for (let j = 0; j < ops.length; j++) {
      if (j === idx) continue;
      if (symplecticAnticommute(sv, opSymp[j], n)) {
        anticommutePairIdx = j;
        break;
      }
    }

    return { op, commutesWithAll, notInStabilizerGroup, anticommutePairIdx };
  });

  const allValid = verifications.every((v) => v.commutesWithAll && v.notInStabilizerGroup && v.anticommutePairIdx !== null);

  return { verifications, allValid };
}

// ── Full analysis bundle ──

export interface AnalysisResult {
  commutativity: CommutativityResult;
  logicalOps: LogicalOperatorResult;
  resolvedCircuits: ResolvedCircuit[];
  schedule: ScheduleEntry[];
  stimCode: string;
  qiskitCode: string;
}

export function runFullAnalysis(code: StabilizerCode): AnalysisResult {
  return {
    commutativity: checkCommutativity(code),
    logicalOps: findLogicalOperators(code),
    resolvedCircuits: resolveCircuits(code),
    schedule: resolveSchedule(code),
    stimCode: compileToStim(code),
    qiskitCode: compileToQiskit(code),
  };
}
